import React, { useEffect, useState, useRef } from "react";
import "./select.scss";
import DownArrowIcon from "../../SVG/down-arrow";
import UpArrowIcon from "../../SVG/up-arrow";
import TickIcon from "../../SVG/tick";

interface Option {
  value: string;
  label: string;
}

interface SelectProps {
  required?: boolean;
  id: string;
  onOptionChange: (data: OptionChangeData) => void;
  error?: string;
  label: string;
  placeholder: string;
  hint?: string;
  defaultValue?: string;
  options: Option[];
  value?: string;
  resetValue?: boolean;
  shouldOmitPrevSelectedValueOnReset?: boolean;
}

interface OptionChangeData {
  name: string;
  value: string;
}

export function Select(props: SelectProps) {
  const [selectedValue, setSelectedValue] = useState<Option>({
    value: "",
    label: "",
  });
  const [openOptions, setOpenOptions] = useState(false);
  const [error, setError] = useState("");
  const selectValueRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<string>("bottom");

  useEffect(() => {
    if (props.value) {
      findValue(props.value);
    } else if (props.defaultValue) {
      findValue(props.defaultValue);
    }

    setError(props.error ?? "");
  }, [props.error, props.defaultValue, props.value]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        selectValueRef.current &&
        !selectValueRef.current.contains(event.target as Node) &&
        optionsRef.current &&
        !optionsRef.current.contains(event.target as Node)
      ) {
        setOpenOptions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectValueRef, optionsRef]);

  useEffect(() => {
    if (props.shouldOmitPrevSelectedValueOnReset) {
      setSelectedValue({ value: "", label: "" });
    } else {
      if (props.defaultValue) {
        findValue(props.defaultValue);
      } else {
        setSelectedValue({ value: "", label: "" });
      }
    }
  }, [props.resetValue]);

  const handleClick = () => {
    if (openOptions) {
      setOpenOptions(false);
      setPosition("");
    } else {
      if (selectValueRef.current) {
        const bottom =
          selectValueRef.current.getBoundingClientRect().bottom || 0;
        const fullHeight = window.innerHeight;
        const availableSpaceBelow = fullHeight - bottom;
        const openPosition =
          availableSpaceBelow < fullHeight * 0.45 ? "top" : "bottom";
        setPosition(openPosition);
        setOpenOptions(true);
      } else {
        console.error("selectValueRef.current is null");
      }
    }
  };

  const handleOptionSelect = (option: Option) => {
    setSelectedValue(option);
    setOpenOptions(false);
    const data = {
      name: props.id,
      value: option.value,
    };
    props.onOptionChange(data);
  };

  const findValue = (value: string) => {
    const option = props.options.find((option) => option.value === value);
    if (option) {
      setSelectedValue(option);
    }
  };

  return (
    <div className="custom-select-container">
      <div className={error ? "custom-select error" : "custom-select"}>
        <div className="label">
          {props.label} {props.required && <span className="required"> *</span>}
        </div>
        <div
          className={openOptions ? "selected-text open" : "selected-text"}
          onClick={handleClick}
          ref={selectValueRef}
        >
          {selectedValue.label === "" ? (
            <div className="placeholder">{props.placeholder}</div>
          ) : (
            <div className="selected-value">{selectedValue.label}</div>
          )}

          {openOptions ? <UpArrowIcon /> : <DownArrowIcon />}
        </div>
        <div className="select-hint">{props.hint}</div>
        {error && <div className="error">{error}</div>}
        {openOptions && (
          <div
            className={
              position === "bottom"
                ? "custom-options bottom"
                : "custom-options top"
            }
            ref={optionsRef}
          >
            {props.options.map((option) => (
              <div
                key={option.value}
                className={
                  selectedValue.value === option.value
                    ? "option selected"
                    : "option"
                }
                onClick={() => handleOptionSelect(option)}
              >
                {option.label}
                {selectedValue.value === option.value && <TickIcon />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
